import { CSSProperties } from 'react'
import { WATERMARK_MARGIN } from '../../../../constants'
import CreationToolWatermarkPosition from '../../../../types/watermark'
import { ICapture, ISegment } from '../../shared/types'

const getCropForAspectRatio = (width, height, aspectRatio) => {
    const [numerator, denominator] = aspectRatio.split(':')
    const ratio = numerator / denominator

    let cropWidth
    let cropHeight

    if (ratio < 1) {
        // "tall" crop
        cropWidth = Math.min(height * ratio, width)
        cropHeight = cropWidth / ratio
    } else {
        // "wide" or square crop
        cropHeight = Math.min(width / ratio, height)
        cropWidth = cropHeight * ratio
    }

    const cropX = width / 2 - cropWidth / 2
    const cropY = height / 2 - height / 2

    return {
        x: cropX,
        y: cropY,
        w: cropWidth,
        h: cropHeight,
    }
}

const base64ToBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data.replace('data:image/png;base64,', ''))
    const byteArrays: Uint8Array[] = []

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize)

        const byteNumbers = new Array(slice.length)
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        byteArrays.push(byteArray)
    }

    const blob = new Blob(byteArrays, { type: contentType })
    return blob
}

const getCaptureWatermarkPreviewStyles = (
    { width, watermark, upload_metadata }: ICapture,
    { editParams, width: segWidth }: ISegment,
    scale: number = 1
): CSSProperties => {
    const segmentWidth = editParams?.crop?.width || segWidth || width
    const watermarkWidth = upload_metadata?.watermark_width
    const watermarkPosition = upload_metadata?.watermark_position

    if (!watermark || !watermarkWidth || !watermarkPosition) {
        return {}
    }

    const margin = Math.round(segmentWidth * WATERMARK_MARGIN * scale)
    const baseStyles: CSSProperties = {
        position: 'absolute',
        width: `${Math.round((parseInt(watermarkWidth || '0') / width) * 100)}%`,
    }

    switch (watermarkPosition) {
        case CreationToolWatermarkPosition.BottomLeft:
            return { ...baseStyles, left: margin, bottom: margin }
        case CreationToolWatermarkPosition.BottomRight:
            return { ...baseStyles, right: margin, bottom: margin }
        case CreationToolWatermarkPosition.TopLeft:
            return { ...baseStyles, left: margin, top: margin }
        case CreationToolWatermarkPosition.TopRight:
            return { ...baseStyles, right: margin, top: margin }
    }
}

const preloadImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = document.createElement('img')
        img.onload = () => resolve(img)
        img.onerror = reject
        img.crossOrigin = 'anonymous'
        img.src = url
    })

const getMergedWatermark = async (segment: ISegment, capture: ICapture): Promise<string> => {
    const { editParams } = segment
    const { upload_metadata: uploadMetadata } = capture
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    canvas.width = editParams?.crop?.width || capture.width
    canvas.height = editParams?.crop?.height || capture.height

    if (editParams?.watermark) {
        const segmentWatermark = await preloadImage(editParams.watermark.imageUrl)
        context?.drawImage(segmentWatermark, 0, 0)
    }

    if (capture.watermark && uploadMetadata?.watermark_width && uploadMetadata.watermark_position) {
        const img = await preloadImage(capture.watermark.imageUrl)
        const scale = canvas.width / capture.width
        const width = parseInt(uploadMetadata?.watermark_width) * scale
        const height = width / (img.naturalWidth / img.naturalHeight)
        const margin = canvas.width * WATERMARK_MARGIN
        let x = canvas.width - width - margin
        let y = canvas.height - height - margin

        if (uploadMetadata.watermark_position.indexOf('LEFT') > -1) {
            x = margin
        }

        if (uploadMetadata.watermark_position.indexOf('TOP') > -1) {
            y = margin
        }

        context?.drawImage(img, x, y, width, height)
    }

    return canvas.toDataURL('image/png', 1)
}

export { getCropForAspectRatio, base64ToBlob, getCaptureWatermarkPreviewStyles, getMergedWatermark }
